import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import { useInView } from 'react-intersection-observer';
import { animated } from 'react-spring';
import classNames from 'classnames';

import { IPressListFields } from '../../../types/contentful';

import { ColorTokenProp } from '../../theme/tokens/color';

import { PressPost } from '../PressPost';
import { ScrollReveal } from '../ScrollReveal';

const HiddenBox = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof animated.div> & {
    hidden: boolean;
    firstPressItem: boolean;
  }
>(function HiddenBox({ hidden, firstPressItem, children, ...props }, ref) {
  return (
    <animated.div
      ref={ref}
      className={classNames(
        hidden ? 'hidden' : 'block',
        firstPressItem ? 'mt-0' : 'mt-6',
        'tablet:mt-6 desktop:mb-8 desktop:mt-6'
      )}
      {...props}
    >
      {children}
    </animated.div>
  );
});

const colors: ColorTokenProp[] = ['pressHeaderBg1', 'pressHeaderBg2'];
const colorPattern = [...colors, ...[...colors].reverse()];
const NUMBER_TO_SHOW = 4;

export function PressList({ headingText, pressList }: IPressListFields) {
  const totalPressLength = pressList?.length || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState<boolean | undefined>();
  const [focusedPostIndex, setFocusedPostIndex] = useState<
    number | undefined
  >();

  const getColor = (index: number) => colorPattern[index % colorPattern.length];

  const { ref } = useInView({
    triggerOnce: true
  });

  useEffect(() => {
    setCanLoadMore((currentIndex + 1) * NUMBER_TO_SHOW < totalPressLength);
  }, [currentIndex]);

  return (
    <ScrollReveal>
      <div
        className="block-container mb-12 mt-0 tablet:mb-16 tablet:mt-10 desktop:mb-25 desktop:mt-16"
        data-blockid="pressList"
        data-testid="press-list"
      >
        <div className="layout-padding flex w-full flex-col items-center">
          <div className="block-layout-gutter w-full max-w-screen-max">
            <div className="box-column hidden w-full pb-0 tablet:block desktop:w-10/12">
              <h2 className="typography-display-2 mb-0 text-black">
                {headingText}
              </h2>
            </div>
            <div className="box-column w-full pt-4 tablet:pt-6 desktop:w-10/12 desktop:pb-6 desktop:pt-8">
              <div className="masonry-wrapper">
                <Masonry
                  breakpointCols={{ default: 2, 719: 1 }}
                  className="masonry-grid"
                  columnClassName="masonry-grid-column"
                >
                  {(pressList || []).map(({ fields }, index) => (
                    <HiddenBox
                      key={index}
                      firstPressItem={index === 0 ? true : false}
                      hidden={index > (currentIndex + 1) * NUMBER_TO_SHOW - 1}
                      data-testid={`press-post-${index}`}
                      ref={ref}
                    >
                      <PressPost
                        {...fields}
                        color={getColor(index)}
                        focused={index === focusedPostIndex}
                      />
                    </HiddenBox>
                  ))}
                </Masonry>
              </div>

              {canLoadMore && (
                <div className="mt-10 text-center desktop:mt-12">
                  <button
                    type="button"
                    onClick={() => {
                      const newIndex = currentIndex + 1;
                      setCurrentIndex(newIndex);
                      setFocusedPostIndex(newIndex * NUMBER_TO_SHOW);
                    }}
                    className="button-dark"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
}
